"use client";

import { useState } from "react";
import { Check } from "lucide-react";

export function EmailCapture() {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "preview" | "error">("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;

    setStatus("loading");
    try {
      const res = await fetch("/api/email/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, website }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? "Subscription failed");
      }
      const data = await res.json().catch(() => ({})) as { dev?: boolean };
      setStatus(data.dev ? "preview" : "success");
      setEmail("");
      setWebsite("");
    } catch {
      setStatus("error");
    }
  }

  return (
    <section
      className="py-20 border-t"
      style={{ backgroundColor: "#141414", borderColor: "#2A2A2A" }}
    >
      <div className="container-main">
        <div className="max-w-xl mx-auto text-center">
          <p
            className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
            style={{ color: "#E8C547" }}
          >
            Stay in tune
          </p>
          <h2
            className="text-3xl sm:text-4xl font-black mb-3"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Harmony in your inbox.
          </h2>
          <p className="mb-8" style={{ color: "#9A9A9A" }}>
            New colors, exclusive deals, and artist stories — straight to you.
          </p>

          {status === "success" || status === "preview" ? (
            <div
              className="px-6 py-4 rounded-md text-sm font-medium"
              style={{ backgroundColor: status === "preview" ? "#E8C54720" : "#40C07020", color: status === "preview" ? "#E8C547" : "#40C070", border: `1px solid ${status === "preview" ? "#E8C54740" : "#40C07040"}` }}
            >
              <span className="inline-flex items-center gap-2">
                <Check size={16} />
                {status === "preview" ? "Preview only — not subscribed." : "You're in! Watch your inbox for something good."}
              </span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
              <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
                <label htmlFor="newsletter-website">Website</label>
                <input
                  id="newsletter-website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="flex-1 px-4 py-3 text-sm rounded-sm border outline-none transition-colors focus:border-[#E8C547]"
                style={{
                  backgroundColor: "#1E1E1E",
                  borderColor: "#3A3A3A",
                  color: "#F5F5F5",
                }}
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="px-6 py-3 text-sm font-bold rounded-sm transition-all hover:brightness-110 disabled:opacity-60 whitespace-nowrap"
                style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
              >
                {status === "loading" ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
          )}

          {status === "error" && (
            <p className="mt-3 text-sm" style={{ color: "#E84040" }}>
              Something went wrong. Please try again.
            </p>
          )}

          <p className="mt-4 text-xs" style={{ color: "#8A8A8A" }}>
            No spam. Unsubscribe any time.
          </p>
        </div>
      </div>
    </section>
  );
}
